import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Paper,
  Skeleton
} from '@mui/material';
import NoDataFound from "../../../dashboardcomponents/Traffic/NoDataFound";

export default function TagUsage({
  title,
  description,
  data = [],
  bgcolor,
  loading = false,
  value = 'Count'
}) {
  const [tooltip, setTooltip] = useState(null);

  // Find max value for scaling
  const maxValue = data.length > 0 ? Math.max(...data.map(item => item.count)) : 0;
  const total = data.reduce((sum, d) => sum + d.count, 0, 0);

  const handleHover = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX;
    let tooltipLeft = mouseX < window.innerWidth / 2
      ? mouseX - 184
      : mouseX + 16;

    tooltipLeft = Math.max(10, Math.min(tooltipLeft, window.innerWidth - 184 - 10));

    const tooltipTop = rect.top + rect.height / 2;

    setTooltip({
      position: { x: tooltipLeft, y: tooltipTop },
      data: item,
    });
  };

  const handleLeave = () => {
    setTooltip(null);
  };

  const getTooltipData = (item) => {
    const percent = Math.round((item.count / total) * 100);
    return {
      title,
      name: item.name,
      count: item.count,
      percent,
    };
  };

  const hasData = data.length > 0;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '8px',
        p: 0,
        maxHeight: '488px',
        width: '100%',
        border: '1px solid #E5E9EC',
      }}
    >
      <CardContent sx={{ p: '24px', m: 0, height: '100%' }}>
        <Typography
          sx={{
            color: '#061522',
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '18px',
            mb: 1,
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            sx={{
              color: '#405261',
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              mb: 2,
            }}
          >
            {description}
          </Typography>
        )}

        {/* Column Headers – only show when we have data */}
        {hasData && !loading && (
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                color: '#405261',
                fontFamily: 'Inter',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
              }}
            >
              Name
            </Typography>
            <Typography
              sx={{
                color: '#405261',
                fontFamily: 'Inter',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
              }}
            >
              {value}
            </Typography>
          </Stack>
        )}

        <Box sx={{ position: 'relative', flex: 1 }}>
          {loading ? (
            // Loading skeletons
            <Stack spacing={3}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Stack
                  key={index}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  <Skeleton
                    variant="rectangular"
                    width={Math.random() * 120 + 80}
                    height={48}
                    sx={{ borderRadius: '8px' }}
                  />
                  <Skeleton variant="text" width={60} height={18} />
                </Stack>
              ))}
            </Stack>
          ) : hasData ? (
            // Actual data bars
            <Stack spacing={3}>
              {data.map((item, index) => (
                <Stack
                  key={index}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={3}
                >
                  <Box
                    onMouseEnter={(e) => handleHover(e, item)}
                    onMouseLeave={handleLeave}
                    sx={{
                      width: `${(item.count / maxValue) * 240}px`,
                      minWidth: '80px',
                      maxWidth: '200px',
                      height: '48px',
                      flexShrink: 0,
                      borderRadius: '8px',
                      background: bgcolor,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      position: 'relative',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '18px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '100%',
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      color: '#061522',
                      textAlign: 'right',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '18px',
                      minWidth: '60px',
                    }}
                  >
                    {item.count}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            // ← No data state
            <Box
              sx={{
                height: '240px',           // ← adjust as needed
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <NoDataFound
                containerStyle={{ height: 'auto' }}
                title="No Data Found"
                isForChart={true}
                showBackButton={false}
              />
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Custom Tooltip – only render when there's data & tooltip active */}
      {tooltip && hasData && (
        <Paper
          sx={{
            position: "fixed",
            left: tooltip.position.x + 230,
            top: tooltip.position.y + 30,
            transform: "translateY(-50%)",
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            backgroundColor: "#FFF",
            boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
            p: 2,
            minWidth: 168,
            pointerEvents: "none",
            zIndex: 1300,
          }}
        >
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: 16,
              fontWeight: 500,
              lineHeight: "18px",
              mb: 2,
            }}
          >
            {getTooltipData(tooltip.data).title}
          </Typography>
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "18px",
              mb: 1,
            }}
          >
            {getTooltipData(tooltip.data).name}
          </Typography>
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: 12,
              fontWeight: 500,
              lineHeight: "18px",
            }}
          >
            {getTooltipData(tooltip.data).count}
          </Typography>
        </Paper>
      )}
    </Card>
  );
}